/// <amd-module name="VSSUI/Components/ExpandableTextField/ExpandableTextField" />
import * as React from "react";
import { Expandable, IExpandable, IExpandableChildProps } from "VSSUI/Expandable";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { IconSize } from "VSSUI/Icon";
import { TextField } from "VSSUI/TextField";
import { css, KeyCode } from "VSSUI/Util";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { Location } from "VSSUI/Utilities/Position";
import { IExpandableTextFieldProps } from "./ExpandableTextField.Props";

let textFieldId = 1;

export class ExpandableTextField extends React.Component<IExpandableTextFieldProps> implements IExpandable, IFocusable<{}> {
    public static defaultProps: Readonly<Partial<IExpandableTextFieldProps>> = {
        expandKey: [KeyCode.downArrow, KeyCode.enter]
    };

    private textFieldElement = React.createRef<TextField>();
    private containerElement: React.RefObject<HTMLDivElement>;
    private dropdownId: string;
    private expandable = React.createRef<Expandable>();

    public constructor(props: Readonly<IExpandableTextFieldProps>) {
        super(props);
        this.dropdownId = props.dropdownId || "dropdown-" + textFieldId++;
        this.containerElement = props.containerRef || React.createRef<HTMLDivElement>();
    }

    public render(): JSX.Element {
        return (
            <Expandable
                disabled={this.props.disabled}
                expandKey={this.props.expandKey}
                onCollapse={this.props.onCollapse}
                onExpand={this.props.onExpand}
                renderCallout={this.renderCallout}
                ref={this.expandable}
            >
                {(expandableProps: IExpandableChildProps) => {
                    return (
                        <FocusWithin onFocus={this.props.onFocus} onBlur={this.props.onBlur}>
                            {(focusStatus: IFocusWithinStatus) => (
                                <div
                                    className={css(this.props.className, "bolt-expandable-textfield")}
                                    onBlur={() => {
                                        if (this.props.blurDismiss) {
                                            this.collapse();
                                        }

                                        focusStatus.onBlur && focusStatus.onBlur();
                                    }}
                                    onFocus={(event: React.FocusEvent<HTMLElement>) => {
                                        focusStatus.onFocus && focusStatus.onFocus(event);

                                        // If the top-level component recieves focus, set focus to the text field.
                                        if (event.target === this.containerElement.current) {
                                            this.focus();
                                        }
                                    }}
                                    onMouseDown={expandableProps.onMouseDown}
                                    onKeyDown={expandableProps.onKeyDown}
                                    ref={this.containerElement}
                                    tabIndex={-1}
                                    role={this.props.role}
                                >
                                    <TextField
                                        ariaHasPopup="dialog"
                                        {...this.props}
                                        ariaActiveDescendant={expandableProps.expanded ? this.props.ariaActiveDescendant : undefined}
                                        role={this.props.editable ? "combobox" : undefined}
                                        ariaExpanded={expandableProps.expanded}
                                        ariaControls={expandableProps.expanded ? this.dropdownId : undefined}
                                        className=""
                                        onClick={expandableProps.onClick}
                                        ref={this.textFieldElement}
                                        suffixIconProps={
                                            !this.props.hideDropdownIcon
                                                ? {
                                                      key: "dropdown-icon",
                                                      className: css("bolt-expandable-textfield-icon icon-right", this.props.disabled && "disabled"),
                                                      iconName: "ChevronDownMed",
                                                      onClick: expandableProps.onClick,
                                                      size: IconSize.small
                                                  }
                                                : undefined
                                        }
                                    />
                                </div>
                            )}
                        </FocusWithin>
                    );
                }}
            </Expandable>
        );
    }

    public collapse = (): void => {
        if (this.expandable.current) {
            this.expandable.current.collapse();
        }
    };

    public expand = (): void => {
        if (this.expandable.current) {
            this.expandable.current.expand();
        }
    };

    public focus(): void {
        if (this.textFieldElement.current) {
            this.textFieldElement.current.focus();
        }
    }

    public select(): void {
        if (this.textFieldElement.current) {
            this.textFieldElement.current.select();
        }
    }

    private renderCallout = () => {
        return this.props.renderCallout(
            this,
            this.dropdownId,
            this.props.anchorElement
                ? this.props.anchorElement
                : !this.props.anchorPoint
                ? this.containerElement.current
                    ? this.containerElement.current
                    : undefined
                : undefined,
            this.props.anchorOffset || { horizontal: 0, vertical: 0 },
            this.props.anchorOrigin || { horizontal: Location.end, vertical: Location.end },
            this.props.anchorPoint,
            this.props.dropdownOrigin || { horizontal: Location.end, vertical: Location.start }
        );
    };
}
